import type { CellPos, CellRect, DraftGridLens } from "@heraion/shared";

import { actionsBundle, type GridAction, type GridGestureBundle } from "../IntensityGrid/useGridGestures";
import type { TFn } from "../i18nTypes";
import type { SheetMenuItem } from "../primitives/SheetMenu";
import { gridRangeMenuItems, RANGE_ACTIONS, rangeRefusal, type RangeAction } from "./gridRangeMenuItems";
import type { GridClipboard } from "./useGridClipboard";
import type { useGridSelection } from "./useGridSelection";
import { cellWhy, type RoundCellWrites } from "./useRoundCellWrites";

// The wide grid's range actions for a finger and a screen reader (#336 slice 6
// part 3): the menu's items, and the named actions on a cell. The keys have
// their own door (useGridKeys); all three end in the same block writes. With
// no writes (a grid read only, #358) there are none.
export function useGridRangeActions({
  t,
  lens,
  sel,
  cells,
  clipboard,
  showToast,
}: {
  t: TFn;
  lens: DraftGridLens;
  sel: ReturnType<typeof useGridSelection>;
  cells: RoundCellWrites | null;
  clipboard: GridClipboard;
  showToast: ((message: string) => void) | undefined;
}): { menuItems: SheetMenuItem[]; cellGesture: (pos: CellPos) => GridGestureBundle } {
  if (!cells || !showToast) return { menuItems: [], cellGesture: () => ({}) };
  const rect: CellRect | null = sel.rect;
  const why = (r: Parameters<typeof cellWhy>[1]) => cellWhy(t, r);
  const refused = () => showToast(t("editorV3.grid.menu.clipboardRefused", { defaultValue: "The browser did not allow the clipboard." }));
  const run = (r: CellRect): Record<RangeAction, () => void> => ({
    copy: () => {
      void clipboard.write(cells.copy(lens, r, true)).then((ok) => {
        if (ok) cells.copied(r);
        else refused();
      });
    },
    paste: () => {
      void clipboard.read().then((text) => {
        if (text === null) refused();
        else cells.paste(lens, r, text);
      });
    },
    fillDown: () => cells.fill(lens, r, "down"),
    fillRight: () => cells.fill(lens, r, "right"),
  });

  const menuItems: SheetMenuItem[] = rect
    ? gridRangeMenuItems({ t, why, lens, rect, canRead: clipboard.canRead, run: run(rect) })
    : [];

  /** The named actions of the cell at `pos`, and the hold that enters the mode. */
  const cellGesture = (pos: CellPos) => {
    const actions: GridAction[] = [
      { name: "select", label: t("editorV3.grid.act.select", { defaultValue: "Start selecting here" }), run: () => sel.startSelecting(pos) },
    ];
    if (rect && sel.selecting && sel.anchored) {
      actions.push({ name: "extend", label: t("editorV3.grid.act.extend", { defaultValue: "Extend the selection to here" }), run: () => sel.extendTo(pos) });
    }
    if (rect) {
      const go = run(rect);
      const labels: Record<RangeAction, string> = {
        copy: t("editorV3.grid.act.copy", { defaultValue: "Copy the selection" }),
        paste: t("editorV3.grid.act.paste", { defaultValue: "Paste on the selection" }),
        fillDown: t("editorV3.grid.act.fillDown", { defaultValue: "Fill the selection down" }),
        fillRight: t("editorV3.grid.act.fillRight", { defaultValue: "Fill the selection right" }),
      };
      for (const action of RANGE_ACTIONS) {
        if (rangeRefusal({ t, why, action, lens, rect, canRead: clipboard.canRead }) === null) {
          actions.push({ name: action, label: labels[action], run: go[action] });
        }
      }
    }
    return actionsBundle(actions, () => sel.startSelecting(pos));
  };

  return { menuItems, cellGesture };
}
